import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  TrashIcon,
  ArrowPathIcon,
  CloudIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';
import { farmApi, indicesApi, weatherApi, alertsApi, analysisApi } from '../lib/api';
import { format, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import { INDEX_CONFIG, INDEX_CATEGORIES, getIndexColor, getIndexName } from '../lib/indexConfig';
import ExportModal from '../components/ExportModal';

export default function FarmDetail() {
  const { farmId } = useParams<{ farmId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedIndex, setSelectedIndex] = useState('ndvi');
  const [dateRange, setDateRange] = useState('30');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { data: farm, isLoading } = useQuery({
    queryKey: ['farm', farmId],
    queryFn: () => farmApi.get(farmId!),
    enabled: !!farmId,
  });

  const { data: indexData } = useQuery({
    queryKey: ['indices', farmId, selectedIndex, dateRange],
    queryFn: () => {
      const end = new Date().toISOString().split('T')[0];
      const start = subDays(new Date(), parseInt(dateRange)).toISOString().split('T')[0];
      return indicesApi.getTimeSeries(farmId!, selectedIndex, start, end);
    },
    enabled: !!farmId,
  });

  const { data: weatherData } = useQuery({
    queryKey: ['weather', farmId],
    queryFn: () => weatherApi.getHistory(farmId!),
    enabled: !!farmId,
  });

  const { data: alertsData } = useQuery({
    queryKey: ['alerts', farmId],
    queryFn: () => alertsApi.getByFarm(farmId!),
    enabled: !!farmId,
  });

  const { data: classification } = useQuery({
    queryKey: ['classification', farmId],
    queryFn: () => analysisApi.getClassification(farmId!),
    enabled: !!farmId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => farmApi.delete(farmId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['farms'] });
      toast.success('Farm deleted');
      navigate('/farms');
    },
  });

  const analysisMutation = useMutation({
    mutationFn: () => analysisApi.runAnalysis({
      farmId: farmId!,
      type: 'full',
      startDate: subDays(new Date(), 30).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
    }),
    onSuccess: (data) => toast.success(`Analysis started: ${data.jobId}`),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!farm) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Farm not found</p>
        <Link to="/farms" className="text-green-600 hover:underline">Back to farms</Link>
      </div>
    );
  }

  const chartData = indexData?.data?.map((d) => ({
    date: format(new Date(d.date), 'MMM d'),
    value: d.meanValue,
  })) || [];

  const weatherChartData = weatherData?.data?.slice(-7).map((d) => ({
    date: format(new Date(d.date), 'EEE'),
    temp: Math.round((d.temperatureMax + d.temperatureMin) / 2),
    rain: Math.round(d.precipitation),
  })) || [];

  const selectedIndexColor = getIndexColor(selectedIndex);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/farms" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{farm.name}</h1>
            <p className="text-gray-500">
              {farm.areaHectares?.toFixed(1)} ha • {farm.cropType || 'Unknown crop'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsExportModalOpen(true)}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            Export
          </button>
          <button
            onClick={() => analysisMutation.mutate()}
            disabled={analysisMutation.isPending}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-5 w-5 mr-2 ${analysisMutation.isPending ? 'animate-spin' : ''}`} />
            Run Analysis
          </button>
          <button
            onClick={() => {
              if (confirm('Delete this farm?')) deleteMutation.mutate();
            }}
            className="flex items-center px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
          >
            <TrashIcon className="h-5 w-5 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Current NDVI</p>
          <p className="text-2xl font-bold text-green-600">
            {chartData.length > 0 ? chartData[chartData.length - 1].value.toFixed(3) : 'N/A'}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Detected Crop</p>
          <p className="text-2xl font-bold text-gray-900">
            {classification?.predictedCrop || farm.cropType || 'Unknown'}
          </p>
          {classification && (
            <p className="text-xs text-gray-500">{(classification.confidence * 100).toFixed(0)}% confidence</p>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Health Score</p>
          <p className="text-2xl font-bold text-blue-600">78/100</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <p className="text-sm text-gray-500">Active Alerts</p>
          <p className="text-2xl font-bold text-orange-600">{alertsData?.alerts?.length || 0}</p>
        </div>
      </div>

      {/* Index Chart */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Vegetation Index Trends</h2>
          <div className="flex items-center space-x-4">
            <select
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm min-w-[200px]"
            >
              {INDEX_CATEGORIES.map((category) => (
                <optgroup key={category.name} label={category.name}>
                  {category.indices.map((indexId) => (
                    <option key={indexId} value={indexId}>
                      {getIndexName(indexId)} - {INDEX_CONFIG[indexId]?.fullName}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="indexGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={selectedIndexColor} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={selectedIndexColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke={selectedIndexColor} fill="url(#indexGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weather */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CloudIcon className="h-5 w-5 mr-2 text-blue-500" />
            Weather (7 Days)
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={weatherChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="temp" name="Temp (°C)" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="rain" name="Rain (mm)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-orange-500" />
            Health Alerts
          </h2>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {alertsData?.alerts?.length ? (
              alertsData.alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-3 rounded-lg border ${
                    alert.severity === 'high' || alert.severity === 'critical'
                      ? 'bg-red-50 border-red-200'
                      : alert.severity === 'medium'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <p className="font-medium text-sm">{alert.message}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(new Date(alert.detectedAt), 'MMM d, h:mm a')}
                  </p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No alerts</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        farmId={farmId!}
        farmName={farm.name}
      />
    </div>
  );
}
